import { useEffect, useMemo, useRef, useState } from "react";
import { bindActionCreators } from "redux";
import { editorReduxStore, type AppDispatch } from "./store";
import { editorThunks, type EditorThunks } from "./thunks";
import type { EditorSliceState } from "./types";

export function useEditorDispatch(): AppDispatch {
  return editorReduxStore.dispatch;
}

export function getEditorState(): EditorSliceState {
  return editorReduxStore.getState().editor;
}

export function useEditorActions(): EditorThunks {
  const dispatch = useEditorDispatch();
  return useMemo(() => bindActionCreators(editorThunks, dispatch), [dispatch]);
}

export function useEditorStore<T>(selector: (state: EditorSliceState) => T): T {
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const [selected, setSelected] = useState(() =>
    selectorRef.current(editorReduxStore.getState().editor),
  );

  useEffect(() => {
    let current = selectorRef.current(editorReduxStore.getState().editor);
    return editorReduxStore.subscribe(() => {
      const next = selectorRef.current(editorReduxStore.getState().editor);
      if (!Object.is(next, current)) {
        current = next;
        setSelected(next);
      }
    });
  }, []);

  return selected;
}
